import { Node, Mark, Extension, mergeAttributes, InputRule } from '@tiptap/core'
import type { ResolvedPos, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { TextSelection } from '@tiptap/pm/state'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import { splitBlock } from '@tiptap/pm/commands'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Heading from '@tiptap/extension-heading'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Code from '@tiptap/extension-code'
import Link from '@tiptap/extension-link'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import CodeBlock from '@tiptap/extension-code-block'
import Blockquote from '@tiptap/extension-blockquote'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import HardBreak from '@tiptap/extension-hard-break'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import History from '@tiptap/extension-history'
import Dropcursor from '@tiptap/extension-dropcursor'
import Gapcursor from '@tiptap/extension-gapcursor'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { HintView } from './nodeviews/HintView'
import { FieldView } from './nodeviews/FieldView'
import { ExpandableView } from './nodeviews/ExpandableView'
import { TabsView, TabView } from './nodeviews/TabsView'
import { CardsView, CardView } from './nodeviews/CardsView'
import { StepsView, StepView } from './nodeviews/StepsView'
import { FigureView } from './nodeviews/FigureView'
import { EmbedView } from './nodeviews/EmbedView'
import { FileView } from './nodeviews/FileView'
import { ButtonView } from './nodeviews/ButtonView'
import { CodeGroupView } from './nodeviews/CodeGroupView'
import { SnippetView } from './nodeviews/SnippetView'
import { UnknownView, GenericDirectiveView } from './nodeviews/UnknownView'
import { moveBlock } from './block-moves'

/**
 * TipTap extensions matched to `docSchema` in @staleless/markdown, node name for
 * node name and attribute for attribute.
 *
 * That correspondence is the whole design. The mdast <-> ProseMirror bridge is
 * property-tested against `docSchema`, so as long as the editor produces
 * documents in that shape, a save cannot lose content. Renaming a node here
 * without renaming it there silently breaks the round trip — which is exactly
 * the class of bug the tests exist to catch, so run them after touching this.
 *
 * TipTap's own marks are called `bold` and `italic`; ours are `strong` and `em`,
 * matching mdast. They are renamed rather than mapped at the boundary, because a
 * mapping layer is one more place for the two to drift.
 */

const Strong = Bold.extend({ name: 'strong' })
const Em = Italic.extend({ name: 'em' })

/**
 * Underline and highlight, matching `docSchema` mark for mark.
 *
 * Hand-rolled rather than `@tiptap/extension-underline` /
 * `@tiptap/extension-highlight`: neither ships in this workspace today, and
 * both marks are this simple — a tag, a toggle command every mark gets for
 * free from `toggleMark`, and (for underline, the one editors bind by
 * convention) a shortcut. Markdown has no bare syntax for either, which is why
 * the bridge writes them as directives (`:underline[x]`, `:highlight[x]`)
 * rather than inventing a delimiter that would silently reinterpret `_x_`- or
 * `~~x~~`-shaped text elsewhere on the page.
 */
const Underline = Mark.create({
  name: 'underline',
  parseHTML: () => [{ tag: 'u' }],
  renderHTML: ({ HTMLAttributes }) => ['u', mergeAttributes(HTMLAttributes), 0],
  addKeyboardShortcuts() {
    return { 'Mod-u': () => this.editor.commands.toggleMark(this.name) }
  },
})

const Highlight = Mark.create({
  name: 'highlight',
  parseHTML: () => [{ tag: 'mark' }],
  renderHTML: ({ HTMLAttributes }) => ['mark', mergeAttributes(HTMLAttributes), 0],
  addKeyboardShortcuts() {
    return { 'Mod-Shift-h': () => this.editor.commands.toggleMark(this.name) }
  },
})

/**
 * `meta` alongside the official extension's `language`.
 *
 * Without this, opening ANY page with a titled, line-numbered or
 * highlighted-line code fence in the block editor dropped every one of those
 * options on the next save — silently: ProseMirror only copies attributes a
 * node TYPE declares, so an attribute present in the JSON but absent from the
 * schema is simply not there once `setContent` runs, with no error anywhere.
 * `docSchema` in @staleless/markdown already carries `meta` for exactly the
 * reason explained there; this is the other half, matching it in the editor
 * that actually round-trips the document.
 *
 * `...this.parent?.()` because `CodeBlock` DOES declare a real attribute
 * (`language`, with its own `parseHTML`) — overwriting rather than merging
 * would have swapped one content-loss bug for another.
 */
const CodeBlockWithMeta = CodeBlock.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      meta: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-meta'),
        renderHTML: (attributes) =>
          attributes['meta'] ? { 'data-meta': attributes['meta'] as string } : {},
      },
    }
  },
})

/** Container directives that hold blocks and carry attributes. */
const container = (
  name: string,
  attributes: Record<string, { default: unknown }>,
  content: string,
  view: Parameters<typeof ReactNodeViewRenderer>[0],
) =>
  Node.create({
    name,
    group: 'block',
    content,
    defining: true,
    addAttributes: () => attributes,
    parseHTML: () => [{ tag: `div[data-sls="${name}"]` }],
    renderHTML: ({ HTMLAttributes }) => [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-sls': name }),
      0,
    ],
    addNodeView: () => ReactNodeViewRenderer(view),
  })

const Hint = container('hint', { type: { default: null } }, 'block+', HintView)
const Expandable = container('expandable', { title: { default: null } }, 'block+', ExpandableView)
/**
 * `group` ties this block to the others like it on the page: a reader who
 * chooses npm once chooses it everywhere the page asks. Null is the ordinary
 * case, and stays as plain in the file as it was.
 */
const Tabs = container('tabs', { group: { default: null } }, 'tab+', TabsView)
const Tab = Node.create({
  name: 'tab',
  content: 'block+',
  defining: true,
  addAttributes: () => ({
    title: { default: null },
    /**
     * What this tab IS, when the block is a synchronised one — `npm`, `macos`.
     * Separate from the title because "npm (recommended)" and "npm" are one
     * choice, and null falls back to the title, which is right for the many
     * groups whose labels already are the values.
     */
    value: { default: null },
    /** Opens on this one rather than the first. At most one per group. */
    default: { default: false },
  }),
  parseHTML: () => [{ tag: 'div[data-sls="tab"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'tab' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(TabView),
})
const Steps = container('steps', {}, 'step+', StepsView)
const Step = Node.create({
  name: 'step',
  content: 'block+',
  defining: true,
  addAttributes: () => ({ title: { default: null } }),
  parseHTML: () => [{ tag: 'div[data-sls="step"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'step' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(StepView),
})
const Figure = container(
  'figure',
  {
    src: { default: null },
    alt: { default: null },
    width: { default: null },
    /** The same picture drawn for a dark ground; usually filled in by an import. */
    srcDark: { default: null },
  },
  'block*',
  FigureView,
)
const Embed = container('embed', { url: { default: null } }, 'block*', EmbedView)
const File = container(
  'file',
  { src: { default: null }, name: { default: null } },
  'block*',
  FileView,
)
// `inline*`, not `block+`: the label is one line of text, matching `heading` —
// which is also why `container()`'s content parameter takes a plain string
// rather than being hardcoded to `block+`.
const Button = container(
  'button',
  { href: { default: null }, style: { default: null } },
  'inline*',
  ButtonView,
)
const CodeGroup = container('codeGroup', {}, 'codeBlock+', CodeGroupView)
const Field = container(
  'field',
  {
    name: { default: null },
    type: { default: null },
    required: { default: false },
    default: { default: null },
    in: { default: null },
  },
  'block+',
  FieldView,
)

/**
 * A reference to a shared block in snippets/<name>.md. An atom — the body is
 * shown, never edited here — matching `docSchema` node for node.
 */
const Snippet = Node.create({
  name: 'snippet',
  group: 'block',
  atom: true,
  addAttributes: () => ({ name: { default: '' } }),
  parseHTML: () => [{ tag: 'div[data-sls="snippet"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'snippet' }),
  ],
  addNodeView: () => ReactNodeViewRenderer(SnippetView),
})
const Cards = container('cards', {}, 'card+', CardsView)
const Card = Node.create({
  name: 'card',
  content: 'block*',
  defining: true,
  addAttributes: () => ({
    title: { default: null },
    href: { default: null },
    icon: { default: null },
  }),
  parseHTML: () => [{ tag: 'div[data-sls="card"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'card' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(CardView),
})

/**
 * A directive the schema does not model by name, and any mdast node it does not
 * model at all. Both are carried through untouched rather than dropped: an
 * unrecognised block must survive an edit-and-save cycle, or importing content
 * from anywhere else quietly destroys parts of it.
 */
const GenericDirective = Node.create({
  name: 'genericDirective',
  group: 'block',
  content: 'block*',
  addAttributes: () => ({
    name: { default: 'unknown' },
    attributes: { default: {} },
    kind: { default: 'container' },
  }),
  parseHTML: () => [{ tag: 'div[data-sls="generic"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'generic' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(GenericDirectiveView),
})

const UnknownBlock = Node.create({
  name: 'unknownBlock',
  group: 'block',
  atom: true,
  addAttributes: () => ({ mdast: { default: {} } }),
  parseHTML: () => [{ tag: 'div[data-sls="unknown"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'unknown' }),
  ],
  addNodeView: () => ReactNodeViewRenderer(UnknownView),
})

const UnknownInline = Node.create({
  name: 'unknownInline',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes: () => ({ mdast: { default: {} } }),
  parseHTML: () => [{ tag: 'span[data-sls="unknown-inline"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'span',
    mergeAttributes(HTMLAttributes, { 'data-sls': 'unknown-inline', class: 'sls-inline-unknown' }),
    'raw',
  ],
})

/** GFM tables. `tableCell` carries `header`, matching docSchema — there is no
 * separate header node, so TipTap's own table extension does not fit. */
const Table = Node.create({
  name: 'table',
  group: 'block',
  content: 'tableRow+',
  isolating: true,
  addAttributes: () => ({ align: { default: [] } }),
  parseHTML: () => [{ tag: 'table' }],
  renderHTML: ({ HTMLAttributes }) => ['table', mergeAttributes(HTMLAttributes), ['tbody', 0]],
})

const TableRow = Node.create({
  name: 'tableRow',
  content: 'tableCell+',
  parseHTML: () => [{ tag: 'tr' }],
  renderHTML: () => ['tr', 0],
})

const TableCell = Node.create({
  name: 'tableCell',
  content: 'inline*',
  isolating: true,
  addAttributes: () => ({ header: { default: false } }),
  parseHTML: () => [{ tag: 'td' }, { tag: 'th', attrs: { header: true } }],
  renderHTML: ({ node }) => [node.attrs['header'] ? 'th' : 'td', 0],
})

/** Attributes docSchema carries that TipTap's defaults do not. */
const BulletListWithSpread = BulletList.extend({
  addAttributes: () => ({ spread: { default: false } }),
})
const OrderedListWithSpread = OrderedList.extend({
  addAttributes: () => ({ start: { default: 1 }, spread: { default: false } }),
})
/**
 * A list item that can be a task.
 *
 * `checked` is `null` for an ordinary bullet and a boolean for a task, which is
 * what `docSchema` says and what the markdown bridge round-trips. It rendered as
 * a plain bullet either way, so an imported checklist survived every save and
 * still looked like a list of bullets.
 *
 * The attribute is written to the DOM as well as held in the model. Without that
 * a copy-paste out of the editor and back in loses the distinction, because
 * parsing goes through the HTML.
 */
const ListItemWithChecked = ListItem.extend({
  addAttributes: () => ({
    checked: {
      default: null,
      parseHTML: (element) => {
        const value = element.getAttribute('data-checked')
        return value === null ? null : value === 'true'
      },
      renderHTML: (attributes) =>
        attributes['checked'] === null ? {} : { 'data-checked': String(attributes['checked']) },
    },
  }),
  /**
   * A plain node view, deliberately not a React one.
   *
   * `ReactNodeViewRenderer` wraps every node in its own `<div>`, which put a div
   * between `<ul>` and `<li>`. That renders correctly and is not a list any more:
   * a list whose children are divs loses its semantics for a screen reader, so
   * the fix for "a checklist looks like bullets" would have broken every list in
   * the editor for the people who can least afford it.
   */
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('li')
      // A separate element for the content, because ProseMirror maps positions
      // through `contentDOM` and must find nothing there but the node's content
      // — the checkbox cannot live inside it.
      const content = document.createElement('div')
      content.className = 'be-item'
      dom.appendChild(content)

      let box: HTMLInputElement | null = null

      const paint = (checked: boolean | null) => {
        if (checked === null) {
          dom.removeAttribute('class')
          dom.removeAttribute('data-checked')
          box?.remove()
          box = null
          return
        }
        dom.className = 'be-task'
        dom.setAttribute('data-checked', String(checked))
        if (!box) {
          box = document.createElement('input')
          box.type = 'checkbox'
          box.className = 'be-task__box'
          box.contentEditable = 'false'
          // Otherwise the click moves the selection into the item first, and the
          // caret ends up somewhere the writer did not put it.
          box.addEventListener('mousedown', (event) => event.preventDefault())
          box.addEventListener('change', () => {
            const at = getPos()
            if (!editor.isEditable || typeof at !== 'number') return
            const { state, dispatch } = editor.view
            const item = state.doc.nodeAt(at)
            if (!item) return
            dispatch(
              state.tr.setNodeMarkup(at, undefined, { ...item.attrs, checked: box!.checked }),
            )
          })
          dom.insertBefore(box, content)
        }
        box.checked = checked
        box.disabled = !editor.isEditable
        // The only text this control has. Without it a screen reader announces
        // an unlabelled checkbox.
        box.setAttribute('aria-label', checked ? 'Done' : 'Not done')
      }

      paint(node.attrs['checked'] as boolean | null)

      return {
        dom,
        contentDOM: content,
        update: (updated) => {
          if (updated.type.name !== node.type.name) return false
          paint(updated.attrs['checked'] as boolean | null)
          return true
        },
      }
    }
  },

  addInputRules() {
    return [
      /**
       * `[ ] ` and `[x] ` at the start of a list item make it a task.
       *
       * On the ITEM, not on a paragraph: `- ` already makes a list, so a writer
       * types `- [ ] ` exactly as they would in Markdown. A rule on the
       * paragraph would also have to create the list, and then typing `[ ] ` in
       * ordinary prose — a common thing to write ABOUT syntax — would turn into
       * one.
       */
      new InputRule({
        find: /^\[([ xX])\]\s$/,
        handler: ({ state, range, match }) => {
          const item = findParentOfType(state.selection.$from, this.name)
          if (!item) return null
          state.tr.delete(range.from, range.to).setNodeMarkup(item.pos, undefined, {
            ...item.node.attrs,
            checked: match[1] !== ' ',
          })
        },
      }),
    ]
  },
})

/** The nearest ancestor of a type, with its position. */
function findParentOfType(
  $pos: ResolvedPos,
  typeName: string,
): { node: ProseMirrorNode; pos: number } | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node.type.name === typeName) return { node, pos: $pos.before(depth) }
  }
  return null
}

/**
 * Enter inside an empty paragraph in a container block.
 *
 * Without this, TipTap's default Enter chain reaches `liftEmptyBlock`, which
 * lifts the empty paragraph OUT of the container — and when that paragraph was
 * the container's only child, the now-invalid container is removed with it.
 * Insert a hint from the slash menu, press Enter once, and the hint is gone:
 * one keystroke deleting a block the person just asked for, reproducible in a
 * browser before this command existed.
 *
 * What Enter should mean instead, and does now:
 *  - in the container's LAST empty paragraph: leave the container. The cursor
 *    moves to a fresh paragraph after it — after the outermost wrapper when
 *    paragraphs cannot sit at this level (a tab exits past the tabs block,
 *    a step past the steps block, a card past the cards grid). When that
 *    empty paragraph was the only child it stays, because `block+` requires
 *    it and because the block the person just inserted must survive.
 *  - in an empty paragraph that is NOT last: a plain split, never a lift.
 *
 * A pure ProseMirror command rather than logic inline in a shortcut, so the
 * test suite can run it — and the default chain beside it — against a
 * headless EditorState and fail if either the bug returns or the exit stops
 * working. `ContainerKeys` below is the one-line wiring.
 */
const CONTAINER_BLOCKS = new Set([
  'hint',
  'expandable',
  'tab',
  'step',
  'card',
  'figure',
  'embed',
  'file',
  'genericDirective',
  'field',
])

export function exitEmptyContainer(
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
): boolean {
  const { $from, empty } = state.selection
  if (!empty) return false
  const paragraphType = state.schema.nodes['paragraph']
  if (!paragraphType) return false
  if ($from.parent.type !== paragraphType || $from.parent.content.size > 0) return false
  const depth = $from.depth
  if (depth < 2) return false // a paragraph at the top level is not ours
  const holder = $from.node(depth - 1)
  if (!CONTAINER_BLOCKS.has(holder.type.name)) return false

  if ($from.index(depth - 1) < holder.childCount - 1) {
    // Empty but not last: split in place. Never let `liftEmptyBlock` run —
    // on a middle paragraph it tears the container in two.
    return splitBlock(state, dispatch)
  }

  // Leave past the outermost ancestor that cannot hold a paragraph itself.
  let exitDepth = depth - 1
  while (exitDepth > 1 && !$from.node(exitDepth - 1).type.contentMatch.matchType(paragraphType)) {
    exitDepth -= 1
  }

  if (dispatch) {
    const tr = state.tr
    if (holder.childCount > 1) tr.delete($from.before(depth), $from.after(depth))
    const after = tr.mapping.map($from.after(exitDepth))
    tr.insert(after, paragraphType.create())
    tr.setSelection(TextSelection.create(tr.doc, after + 1))
    tr.scrollIntoView()
    dispatch(tr)
  }
  return true
}

const ContainerKeys = Extension.create({
  name: 'containerKeys',
  addKeyboardShortcuts() {
    return {
      Enter: () => exitEmptyContainer(this.editor.state, this.editor.view.dispatch),
    }
  },
})

/**
 * Moving a block without the mouse.
 *
 * Registered as a keymap rather than in `editorProps.handleKeyDown`, which is
 * where this was written first and where it never fired: the view's own props
 * are consulted after every plugin, and `Mod-Shift-Arrow` is a selection
 * gesture the editor has already claimed by then. A keymap is the mechanism
 * the rest of this file uses and the one that composes with them.
 *
 * Written at the same time as the drag handle and deliberately not after it: a
 * handle that is the only way to move a block makes the mouse the real
 * interface and the keyboard a courtesy, and this is the screen somebody
 * spends their day in. The gutter's menu prints these two keys beside the rows
 * that duplicate them, which is how a person stops needing the menu.
 */
const BlockMoveKeys = Extension.create({
  name: 'blockMoveKeys',
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-ArrowUp': () => moveBlock(this.editor, -1),
      'Mod-Shift-ArrowDown': () => moveBlock(this.editor, 1),
    }
  },
})

export const extensions = [
  Document,
  Paragraph,
  Text,
  Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
  Blockquote,
  BulletListWithSpread,
  OrderedListWithSpread,
  ListItemWithChecked,
  CodeBlockWithMeta.configure({ HTMLAttributes: { class: 'be-code' } }),
  HorizontalRule,
  HardBreak,
  Image.configure({ inline: true, allowBase64: false }),
  Table,
  TableRow,
  TableCell,
  Hint,
  Tabs,
  Tab,
  Expandable,
  Field,
  Steps,
  Step,
  Figure,
  Embed,
  File,
  Button,
  CodeGroup,
  Snippet,
  Cards,
  Card,
  GenericDirective,
  UnknownBlock,
  UnknownInline,
  Strong,
  Em,
  Strike,
  Code,
  Underline,
  Highlight,
  Link.configure({ openOnClick: false, autolink: false }),
  History,
  Dropcursor.configure({ color: 'var(--accent)', width: 2 }),
  Gapcursor,
  ContainerKeys,
  BlockMoveKeys,
  Placeholder.configure({
    placeholder: ({ node }) =>
      node.type.name === 'paragraph' ? "Write, or press '/' for blocks" : '',
  }),
]

export type { Mark }
